import React from 'react'
import type { ProposedPanel } from '@/types/Dashboards.types'

interface PanelProposalCheckCardProps {
  panel: ProposedPanel
  panelIndex: number
  isSelected: boolean
  onToggle: (index: number) => void
  /** Historical / complete sessions: show locked checkmarks, no interaction */
  readOnly?: boolean
}

/**
 * PanelProposalCheckCard — Step 4 of the AI creation flow.
 * Multi-select, never locks. Shows panel title and description with a custom checkbox.
 * Note: Uses a custom checkbox div per brand guide Section 12, Component 3 —
 * do NOT use shadcn Checkbox here.
 *
 * Multiple cards are rendered inside a container:
 * <div className="border border-border rounded-xl overflow-hidden bg-card divide-y divide-border mb-4">
 */
export const PanelProposalCheckCard: React.FC<PanelProposalCheckCardProps> = ({
  panel,
  panelIndex,
  isSelected,
  onToggle,
  readOnly = false,
}) => {
  if (readOnly) {
    void panelIndex
    return (
      <div
        className="flex items-start gap-3 px-3.5 py-3 bg-card cursor-default pointer-events-none select-none"
        aria-label={`${panel.title} (included in dashboard)`}
      >
        <div
          className="w-4 h-4 rounded border-[1.5px] flex-shrink-0 mt-0.5 flex items-center justify-center bg-accent border-accent"
          aria-hidden="true"
        >
          <svg width="9" height="6" viewBox="0 0 9 6" fill="none" aria-hidden="true">
            <path
              d="M1 3l2 2 5-4.5"
              stroke="white"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-primary mb-0.5">{panel.title}</p>
          <p className="text-xs text-secondary leading-relaxed">{panel.description}</p>
        </div>
      </div>
    )
  }

  return (
    <label className="flex items-start gap-3 px-3.5 py-3 bg-card hover:bg-muted cursor-pointer transition-colors duration-150">
      {/* Hidden real checkbox for form semantics */}
      <input
        type="checkbox"
        className="sr-only"
        checked={isSelected}
        onChange={() => onToggle(panelIndex)}
        aria-label={`Include ${panel.title} panel`}
      />
      {/* Custom visual checkbox per brand guide spec */}
      <div
        className={`w-4 h-4 rounded border-[1.5px] flex-shrink-0 mt-0.5 flex items-center justify-center transition-all duration-150
          ${isSelected ? 'bg-accent border-accent' : 'bg-card border-border'}`}
        aria-hidden="true"
      >
        {isSelected && (
          <svg width="9" height="6" viewBox="0 0 9 6" fill="none" aria-hidden="true">
            <path
              d="M1 3l2 2 5-4.5"
              stroke="white"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-primary mb-0.5">{panel.title}</p>
        <p className="text-xs text-secondary leading-relaxed">{panel.description}</p>
      </div>
    </label>
  )
}
